import { authService } from './auth';
import { ApiError } from './auth';

const API_BASE_URL = 'http://localhost:2330';

export interface EmailSignature {
  signature_id: string;
  user_id: string;
  signature: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ErrorResponse {
  error: string;
  message: string;
  details: {
    limit: string;
    type: string;
    size: string;
  }
}

class SignaturesService {
  API_BASE_URL: string;

  constructor() {
    this.API_BASE_URL = API_BASE_URL;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = authService.getAccessToken();
    const url = `${authService.API_BASE_URL}${endpoint}`;
    
    const defaultOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, {
        ...defaultOptions,
        ...options,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as ErrorResponse;
        throw {
          message: errorData.message || `HTTP error! status: ${response.status}`,
          status: response.status,
        } as ApiError;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }

  async getAllSignatures(): Promise<EmailSignature[]> {
    return this.makeRequest<EmailSignature[]>('/api/signatures');
  }

  async createSignature(signature: string): Promise<EmailSignature> {
    return this.makeRequest<EmailSignature>('/api/signatures', {
      method: 'POST',
      body: JSON.stringify({ signature })
    });
  }

  async updateSignature(id: string, signature: string, is_active: boolean): Promise<EmailSignature> {
    return this.makeRequest<EmailSignature>(`/api/signatures/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ signature, is_active })
    });
  }

  async deleteSignature(id: string): Promise<void> {
    return this.makeRequest<void>(`/api/signatures/${id}`, {
      method: 'DELETE'
    });
  }

  async getSignature(id: string): Promise<EmailSignature> {
    return this.makeRequest<EmailSignature>(`/api/signatures/${id}`);
  }

  async activateSignature(id: string): Promise<EmailSignature> {
    return this.makeRequest<EmailSignature>(`/api/signatures/${id}/activate`, {
      method: 'POST'
    });
  }

  async testConnection(): Promise<boolean> {
    try {
      await this.makeRequest('/api/signatures/test');
      return true;
    } catch (error) {
      return false;
    }
  }
}

export const signaturesService = new SignaturesService(); 